import React from "react";
import Box from "@mui/material/Box";
import { TextField, ThemeProvider, createTheme } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import { debounce } from "@mui/material/utils";
import { FormControl, FormHelperText } from "@mui/material";
import { PlaceType } from "../../models/googleMaps";
import { TextField as NewUITextField } from "../TextField/TextField";

function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

export interface GoogleMapsAutoCompleteProps {
  id: string;
  apiKey: string;
  value?: PlaceType | null;
  error?: Boolean;
  helperText?: React.ReactNode;
  // TODO: remove this once the admin page is also refactored to the new UI
  newUIUXStyle?: Boolean;
  onBlur?: () => void;
  onChange?: (value: PlaceType | null) => void;
}

export default function GoogleMapsAutoComplete(props: GoogleMapsAutoCompleteProps) {
  const [value, setValue] = React.useState<PlaceType | null>(props.value ? props.value : null);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState<readonly PlaceType[]>([]);
  const loaded = React.useRef(false);

  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
    },
  });

  const theme = createTheme();

  // added hardcoded api key to attempt to fix dev/test deploys
  // TODO: remove and get new token once network issue resolved
  const googleApiKeyHC = "AIzaSyAApCIYwA9Xuu10bjI-AN-hRAcgeH3Ef_M"; 

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${googleApiKeyHC}&libraries=places`,
        document.querySelector('head'),
        'google-maps',
      );
    }

    loaded.current = true;
  }  

  const fetch = React.useMemo(
    () =>
      debounce(
        (
          request: { input: string },
          callback: (results?: readonly PlaceType[]) => void,
        ) => {
          (autocompleteService.current as any).getPlacePredictions(
            request,
            callback,
          );
        },
        400,
      ),
    [],
  );

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (
        window as any
      ).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <ThemeProvider theme={props.newUIUXStyle ? darkTheme : theme}>
      <FormControl fullWidth error={props.error ? true : false}>
        <Autocomplete
          id={props.id}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.description
          }
          filterOptions={(x) => x}
          options={options}
          autoComplete
          includeInputInList
          filterSelectedOptions
          value={value}
          noOptionsText="No locations"
          onChange={(event: any, newValue: PlaceType | null) => {
            setOptions(newValue ? [newValue, ...options] : options);
            setValue(newValue);
            if (props.onChange) props.onChange(newValue);
          }}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          onBlur={() => {
            if (props.onBlur) props.onBlur();
          }}
          renderInput={(params) => (
            <>
              {props.newUIUXStyle ?
                <NewUITextField {...params} size="md" placeholder="Start typing address" fullWidth error={props.error ? true : false} /> :
                <TextField {...params} label="Venue Street Address" fullWidth error={props.error ? true : false} />
              }
            </>
          )}
          renderOption={(props, option) => {
            const matches =
              option.structured_formatting.main_text_matched_substrings || [];

            const parts = parse(
              option.structured_formatting.main_text,
              matches.map((match: any) => [match.offset, match.offset + match.length]),
            );

            return (
              <li {...props}>
                <Grid container alignItems="center" >
                  <Grid item sx={{ display: 'flex', width: 44 }}>
                    <LocationOnIcon sx={{ color: 'text.secondary' }} />
                  </Grid>
                  <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                    {parts.map((part, index) => (
                      <Box
                        key={index}
                        component="span"
                        sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                      >
                        {part.text}
                      </Box>
                    ))}
                    <Typography variant="body2" color="text.secondary">
                      {option.structured_formatting.secondary_text}
                    </Typography>
                  </Grid>
                </Grid>
              </li>
            );
          }}
        />
        <FormHelperText>{props.helperText}</FormHelperText>
      </FormControl>
    </ThemeProvider>
  );
}